import React, { useState } from "react";
import { Modal, message, Button } from "antd";
import { Html5QrcodeScanner, Html5QrcodeScanType } from "html5-qrcode";
import { getCheckpointByCheckpointService } from "../services/checkpoint";
import { getPatrolCycleBySite } from "../services/patrol";
import { useNavigate } from "react-router-dom";

const qrcodeRegionId = "html5qr-code-full-region";

const QrcodeComponent = ({ open, cancel, haversine }) => {
  const [data, setData] = useState({});
  const [title, setTitle] = useState("สแกนจุดตรวจ: ");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const scannerRef = React.useRef(null);

  const cleanupScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(error => {
        console.error("Failed to clear scanner", error);
      });

      // ทำความสะอาด camera resources ทั้งหมด
      try {
        // หยุดทุก tracks ที่กำลังทำงานอยู่
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            stream.getTracks().forEach(track => track.stop());
          });

        // หยุด video elements ทั้งหมดที่อาจยังค้างอยู่
        const videoElements = document.getElementsByTagName('video');
        Array.from(videoElements).forEach(video => {
          if (video.srcObject) {
            const stream = video.srcObject;
            stream.getTracks().forEach(track => track.stop());
            video.srcObject = null;
          }
        });
      } catch (error) {
        console.error("Error cleaning up camera resources:", error);
      }

      scannerRef.current = null;
    }
  };

  const handleOk = () => {
    if (data?.checkpoint_uuid) {
      cleanupScanner();
      messageApi.open({
        type: "success",
        content: "เริ่มตรวจสอบจุดตรวจ " + data?.checkpoint_name,
      });
      navigate(
        "/patrol/v1?device=" +
        JSON.parse(localStorage.getItem("device"))[2] +
        "&checkpoint=" +
        data?.checkpoint_uuid
      );
      cancel();
    } else {
      messageApi.open({
        type: "error",
        content: "กรุณาสแกนจุดตรวจ",
      });
    }
  };

  const onScanSuccess = async (decodedText) => {
    // Check if scanner is running before proceeding
    // if (!scannerRef.current || scannerRef.current.getState() !== 1) { // 1 = SCANNING state
    //   return;
    // }

    setTitle("กำลังตรวจสอบ: " + decodedText);
    
    try {
      const res = await getCheckpointByCheckpointService(decodedText);
      
      if (!res) {
        messageApi.open({
          type: "error",
          content: "ไม่พบข้อมูลจุดตรวจ",
        });
        // Resume scanning if checkpoint is not found
        if (scannerRef.current) {
          scannerRef.current.resume();
        }
        setTitle("สแกนจุดตรวจ: ");
        return;
      }
      
      // const location = JSON.parse(localStorage.getItem("location"));
      // const { lat, lon } = location;
      // if (res.checkpoint_lat && res.checkpoint_long) {
      //   const distance = haversine(
      //     res.checkpoint_lat,
      //     res.checkpoint_long,
      //     lat,
      //     lon
      //   );
      //   console.log(distance, res.checkpoint_distance_limit);
      //   if (
      //     distance > res.checkpoint_distance_limit &&
      //     res.checkpoint_distance_limit !== 0
      //   ) {
      //     messageApi.open({
      //       type: "error",
      //       content: "ไม่อยู่ในระยะที่กำหนด",
      //     });
      //     // Resume scanning if checkpoint is out of range
      //     if (scannerRef.current) {
      //       scannerRef.current.resume();
      //     }
      //     setTitle("สแกนจุดตรวจ: ");
      //     return;
      //   }
      // }
      
      setData(res);
      setTitle("สแกนจุดตรวจ: " + res?.checkpoint_name);
      // Stop scanning when valid checkpoint is found
      if (scannerRef.current) {
        scannerRef.current.pause();
      }
      navigate(
        "/patrol/v1?device=" +
        JSON.parse(localStorage.getItem("device"))[2] +
        "&checkpoint=" +
        res?.checkpoint_uuid
      );
    } catch (error) {
      console.error("Network error:", error);
      
      // ตรวจสอบว่าเป็น network error หรือไม่
      const isNetworkError = !navigator.onLine || 
                           error.message.includes('Failed to fetch') ||
                           error.message.includes('NetworkError') ||
                           error.message.includes('ERR_INTERNET_DISCONNECTED');
      
      if (isNetworkError) {
        messageApi.open({
          type: "error",
          content: "ไม่สามารถเชื่อมต่ออินเทอร์เน็ตได้ กรุณาตรวจสอบการเชื่อมต่อและลองใหม่",
          duration: 4,
        });
      } else {
        messageApi.open({
          type: "error",
          content: "เกิดข้อผิดพลาดในการตรวจสอบจุดตรวจ กรุณาลองใหม่",
          duration: 4,
        });
      }
      
      // Resume scanning after error
      if (scannerRef.current) {
        scannerRef.current.resume();
      }
      setTitle("สแกนจุดตรวจ: ");
    }
  };

  const onScanError = (error) => {
    console.warn(`QR Code scan failed: ${error}`);
  };

  // เพิ่ม event listeners สำหรับตรวจสอบสถานะการเชื่อมต่อ
  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      messageApi.open({
        type: "success",
        content: "เชื่อมต่ออินเทอร์เน็ตแล้ว",
        duration: 2,
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      messageApi.open({
        type: "warning",
        content: "ไม่มีการเชื่อมต่ออินเทอร์เน็ต การสแกน QR code อาจไม่ทำงาน",
        duration: 4,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [messageApi]);

  React.useEffect(() => {
    if (open) {
      // ตรวจสอบสถานะอินเทอร์เน็ตก่อนเริ่ม scanner
      if (!navigator.onLine) {
        messageApi.open({
          type: "warning",
          content: "ไม่มีการเชื่อมต่ออินเทอร์เน็ต กรุณาตรวจสอบการเชื่อมต่อก่อนสแกน",
          duration: 4,
        });
      }

      const config = {
        fps: 10,
        qrbox: 250,
        disableFlip: false,
        aspectRatio: 1,
        rememberLastUsedCamera: true,
        hideSelectScanType: true,
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
      };

      const html5QrcodeScanner = new Html5QrcodeScanner(
        qrcodeRegionId,
        config,
        /* verbose= */ false
      );

      scannerRef.current = html5QrcodeScanner;
      html5QrcodeScanner.render(onScanSuccess, onScanError);
     
      return () => {
        cleanupScanner();
      };
    }
  }, [open, messageApi]);

  // เพิ่ม cleanup เมื่อ component unmount
  React.useEffect(() => {
    return () => {
      cleanupScanner();
    };
  }, []);

  return (
    <div>
      {contextHolder}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {title}
            {!isOnline && (
              <span style={{ 
                color: '#ff4d4f', 
                fontSize: '12px',
                backgroundColor: '#fff2f0',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #ffccc7'
              }}>
                ออฟไลน์
              </span>
            )}
            {isOnline && (
              <span style={{ 
                color: '#52c41a', 
                fontSize: '12px',
                backgroundColor: '#f6ffed',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #b7eb8f'
              }}>
                ออนไลน์
              </span>
            )}
          </div>
        }
        open={open}
        onOk={handleOk}
        onCancel={cancel}
        footer={[
          <Button key="reload" onClick={() => window.location.reload()}>
            รีโหลด
          </Button>,
          <Button key="cancel" onClick={cancel}>
            ยกเลิก
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            ตกลง
          </Button>
        ]}
      >
        {!isOnline && (
          <div style={{
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '16px',
            color: '#d46b08'
          }}>
            ⚠️ ไม่มีการเชื่อมต่ออินเทอร์เน็ต การสแกน QR code อาจไม่ทำงานตามปกติ
          </div>
        )}
        <div id={qrcodeRegionId} />
      </Modal>
    </div>
  );
};

export default QrcodeComponent;